/**
 * Corporation Resource Histogram audit log — same idea as the piping progress tracker:
 * search, All / Errors / OK, what changed, superadmin revert for planning / colours.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';

const prettyField = (field) => {
  if (!field) return '';
  return String(field)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatVal = (v) => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const parseDetails = (details) => {
  if (!details) return {};
  if (typeof details === 'string') {
    try { return JSON.parse(details); } catch { return {}; }
  }
  return details;
};

export default function FabricationHistogramAuditModal({
  isOpen, onClose, onNavigate, onReverted,
}) {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [isSuperadmin, setIsSuperadmin] = useState(false);
  const [revertingId, setRevertingId] = useState(null);
  const LIMIT = 50;

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: LIMIT, offset: page * LIMIT });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/fab-histogram/audit-log?${params.toString()}`
      );
      if (response.data.success) {
        setEntries(response.data.data.rows || []);
        setTotalRows(response.data.data.totalRows || 0);
      }
    } catch (error) {
      console.error('Error fetching fab histogram audit log:', error);
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, search, statusFilter, page]);

  useEffect(() => {
    if (isOpen) fetchEntries();
  }, [isOpen, fetchEntries]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await authenticatedApiCall(instance, accounts, 'GET', '/auth/permissions-info');
        if (!cancelled && resp.data.success) {
          setIsSuperadmin(!!resp.data.data?.isSuperadmin);
        }
      } catch {
        if (!cancelled) setIsSuperadmin(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, instance, accounts]);

  useEffect(() => { setPage(0); }, [search, statusFilter]);

  if (!isOpen) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-CA', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const getActionBadge = (action) => {
    const styles = {
      import: { bg: '#dbeafe', color: '#1e40af', label: 'Import' },
      export: { bg: '#e0f2fe', color: '#075985', label: 'Export' },
      refresh: { bg: '#ccfbf1', color: '#0f766e', label: 'Refresh' },
      sync: { bg: '#ecfeff', color: '#0e7490', label: 'Sync' },
      edit: { bg: '#fef3c7', color: '#92400e', label: 'Edit' },
      update: { bg: '#fef3c7', color: '#92400e', label: 'Update' },
      delete: { bg: '#fee2e2', color: '#991b1b', label: 'Delete' },
      revert: { bg: '#e0e7ff', color: '#3730a3', label: 'Revert' },
    };
    const s = styles[action] || { bg: '#f3f4f6', color: '#374151', label: action };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
        backgroundColor: s.bg, color: s.color, textTransform: 'uppercase',
      }}>
        {s.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const isError = status === 'error';
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
        backgroundColor: isError ? '#fee2e2' : '#dcfce7',
        color: isError ? '#991b1b' : '#166534', textTransform: 'uppercase',
      }}>
        {isError ? 'Error' : 'OK'}
      </span>
    );
  };

  const targetLabel = (entry) => {
    const details = parseDetails(entry.details);
    if (details.fileName) return details.fileName;
    if (details.replacedAllExcel) return 'All Excel dumps';
    if (entry.entity_type === 'p6') return 'P6 stack';
    if (entry.entity_type === 'capacity') return 'Shop manpower';
    if (entry.entity_type === 'settings') return 'Company defaults';
    if (entry.entity_type === 'calendars') return 'Calendars';
    if (entry.entity_type === 'absenteeism') return 'Absenteeism';
    if (entry.entity_type === 'colors') return 'Legend colours';
    if (entry.entity_id) return entry.entity_id;
    return '';
  };

  const renderWhatChanged = (entry) => {
    if (entry.status === 'error' && entry.error_message) {
      return <span style={{ color: '#b91c1c' }}>{entry.error_message}</span>;
    }
    const details = parseDetails(entry.details);
    const fieldChanges = Array.isArray(details.fieldChanges) ? details.fieldChanges : [];
    if (fieldChanges.length > 0) {
      const shown = fieldChanges.slice(0, 6);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {shown.map((fc, i) => (
            <div key={i} style={{ fontSize: '12px' }}>
              <span style={{ fontWeight: 600, color: '#374151' }}>{prettyField(fc.field)}</span>
              {': '}
              <span style={{ color: '#9ca3af', textDecoration: 'line-through' }}>{formatVal(fc.from)}</span>
              <span style={{ color: '#6b7280' }}>{' \u2192 '}</span>
              <span style={{ color: '#111827', fontWeight: 600 }}>{formatVal(fc.to)}</span>
            </div>
          ))}
          {fieldChanges.length > shown.length && (
            <div style={{ fontSize: '11px', color: '#9ca3af' }}>+{fieldChanges.length - shown.length} more</div>
          )}
        </div>
      );
    }
    const summary = [];
    if (details.replacedAllExcel) summary.push('wiped all Excel dumps');
    if (Array.isArray(details.imported) && details.imported.length) {
      summary.push(`${details.imported.length} job(s)`);
    }
    if (details.importedCount != null) summary.push(`${details.importedCount} P6 schedule(s)`);
    if (details.skippedCount) summary.push(`${details.skippedCount} skipped`);
    if (details.fileName) summary.push(details.fileName);
    if (details.hourTotal != null) summary.push(`${Math.round(details.hourTotal)} h`);
    if (details.revertedAuditId) summary.push(`reverted #${details.revertedAuditId}`);
    if (Array.isArray(details.errors) && details.errors.length) {
      summary.push(`${details.errors.length} failed`);
    }
    if (summary.length) return <span style={{ color: '#6b7280' }}>{summary.join(' · ')}</span>;
    return <span style={{ color: '#9ca3af' }}>—</span>;
  };

  const isRevertable = (entry) =>
    isSuperadmin &&
    ['update', 'edit'].includes(entry.action) &&
    entry.status === 'success' &&
    ['capacity', 'settings', 'calendars', 'absenteeism', 'colors'].includes(entry.entity_type);

  const handleRevert = async (entry) => {
    const fc = parseDetails(entry.details).fieldChanges || [];
    const summary = fc.slice(0, 5).map((c) => `${prettyField(c.field)} -> ${formatVal(c.from)}`).join('\n');
    if (!window.confirm(`Revert this change?\n\nThe following will be restored:\n${summary}${fc.length > 5 ? `\n+${fc.length - 5} more` : ''}`)) {
      return;
    }
    try {
      setRevertingId(entry.id);
      const resp = await authenticatedApiCall(
        instance, accounts, 'POST',
        `/fab-histogram/audit-log/${entry.id}/revert`
      );
      if (resp.data.success) {
        await fetchEntries();
        if (onReverted) onReverted();
      } else {
        window.alert(resp.data.message || 'Revert failed');
      }
    } catch (error) {
      window.alert(error.response?.data?.message || 'Revert failed');
    } finally {
      setRevertingId(null);
    }
  };

  const handleNavigate = (entry) => {
    if (onNavigate) onNavigate(entry);
  };

  const totalPages = Math.ceil(totalRows / LIMIT);
  const filterBtn = (value, label) => (
    <button
      type="button"
      onClick={() => setStatusFilter(value)}
      style={{
        padding: '6px 12px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
        border: '1px solid ' + (statusFilter === value ? '#0f766e' : '#d1d5db'),
        backgroundColor: statusFilter === value ? '#0f766e' : '#fff',
        color: statusFilter === value ? '#fff' : '#374151', fontWeight: '500',
      }}
    >
      {label}
    </button>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '12px', width: '1000px', maxWidth: '95vw', maxHeight: '85vh',
        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #e5e7eb',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, #0f766e 0%, #0e7490 100%)', color: 'white',
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '18px' }}>Corporation Resource Histogram Audit Log</h2>
            <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.9 }}>
              {totalRows} total entries · click a row to open the matching pane
            </p>
          </div>
          <button type="button" onClick={onClose} style={{
            background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer',
          }}>&times;</button>
        </div>

        <div style={{ padding: '12px 24px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <input
              type="text"
              placeholder="Search action, user, file, shop, error..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                width: '100%', padding: '8px 12px 8px 36px',
                borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px',
              }}
            />
            <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}>
              🔍
            </span>
          </div>
          <div style={{ display: 'flex', gap: '6px' }}>
            {filterBtn('', 'All')}
            {filterBtn('error', 'Errors')}
            {filterBtn('success', 'OK')}
          </div>
        </div>

        <div style={{ overflowY: 'auto', maxHeight: 'calc(85vh - 240px)' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>Loading...</div>
          ) : entries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
              {search || statusFilter ? 'No results matching your filters.' : 'No audit log entries yet.'}
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0, zIndex: 1 }}>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Date</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>User</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Action</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Target</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>What changed</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '1px solid #e5e7eb' }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    onClick={() => handleNavigate(entry)}
                    title="Click to open the matching pane"
                    style={{
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: entry.status === 'error' ? '#fef2f2' : 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    <td style={{ padding: '8px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {formatDate(entry.created_at)}
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      {entry.user_email?.split('@')[0] || 'system'}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {getActionBadge(entry.action)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                      {getStatusBadge(entry.status)}
                    </td>
                    <td style={{ padding: '8px 12px', color: '#0f766e', fontWeight: 600, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {targetLabel(entry)}
                    </td>
                    <td style={{ padding: '8px 12px', maxWidth: '380px' }}>
                      {renderWhatChanged(entry)}
                    </td>
                    <td style={{ padding: '8px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {isRevertable(entry) && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); handleRevert(entry); }}
                          disabled={revertingId === entry.id}
                          style={{
                            padding: '4px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            border: '1px solid #99f6e4', borderRadius: '6px',
                            backgroundColor: revertingId === entry.id ? '#f0fdfa' : '#fff', color: '#0f766e',
                          }}
                          title="Restore the previous planning or colour values (superadmin only)"
                        >
                          {revertingId === entry.id ? 'Reverting…' : '↩ Revert'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div style={{
            padding: '12px 24px', borderTop: '1px solid #e5e7eb',
            display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center',
          }}>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => p - 1)}
              style={{
                padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '4px',
                cursor: page === 0 ? 'default' : 'pointer', fontSize: '13px',
                backgroundColor: page === 0 ? '#f3f4f6' : '#fff',
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
              style={{
                padding: '4px 12px', border: '1px solid #d1d5db', borderRadius: '4px',
                cursor: page >= totalPages - 1 ? 'default' : 'pointer', fontSize: '13px',
                backgroundColor: page >= totalPages - 1 ? '#f3f4f6' : '#fff',
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
